import React, { useState } from "react";
import get from "lodash/get";
import { connect, useDispatch, useSelector } from "react-redux";
import { func, bool, string, object } from "prop-types";
import { parseUrl } from "query-string";

import { login, sendOtp, currentUser, oauthAuthorize } from "@quintype/bridgekeeper-js";
import { IS_OPEN_LOGIN_FORM, MEMBER_UPDATED } from "../../store/actions";
import EditIcon from "../../atoms/EditIcon";
import EyeClose from "../../atoms/EyeCloseIcon";
import EyeOpen from "../../atoms/EyeOpenIcon";
import { navigateTo } from "../../../arrow/utils/utils";

import "./forms.m.css";

const LoginBase = ({ onLogin, setActiveTab, customCallbackUrl, userInfo, setUserInfo, setLoading }) => {
  const [showPassword, setShowPassword] = useState(false);

  const qtConfig = useSelector((state) => get(state, ["qt"], {}));
  const publisherAttributes = get(qtConfig, ["config", "publisher-attributes"], {});
  const currentPath = get(qtConfig, ["currentPath"], "");
  const clientId = get(publisherAttributes, ["sso_login", "client_id"], "");
  const ssoLoginIsEnable = get(publisherAttributes, ["sso_login", "is_enable"], false);
  const [error, setError] = useState({});
  const dispatch = useDispatch();

  const getCurrentUser = async () => {
    try {
      const currentUserResp = await currentUser();
      if (currentUserResp) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "dl_login_success",
          login_status: "logged-in",
          user_id: `${currentUserResp?.user?.id}`,
          subscription_status: "not-subscribed",
        });
      }
      dispatch({ type: MEMBER_UPDATED, member: get(currentUserResp, ["user"], null) });
    } catch (err) {
      console.log("error--------", err);
    }
  };

  const setData = (e) => {
    const userObj = { ...userInfo };
    const fieldName = e.target.id;
    userObj[fieldName] = e.target.value;
    setUserInfo(userObj);
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const loginHandler = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const userObj = {
      username: userInfo.email,
      email: userInfo.email,
      password: userInfo.password,
    };

    if (userInfo.email.length < 1 || userInfo.password.length < 1) {
      const errorMessage = "Please provide username and password";
      setError({ message: errorMessage });

      // Track login error for empty fields
      if (typeof window !== "undefined" && window.dataLayer) {
        window.dataLayer.push({
          event: "dl_login_error",
          event_action: "form_submit",
          error_message: errorMessage,
        });
      }
      return null;
    }

    // Track password entered event
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({
        event: "dl_login_steps",
        event_action: "password_entered",
      });
    }
    setLoading(true);

    login(userObj)
      .then(async ({ user, message }) => {
        if (!user) {
          // Track Login Failure
          if (typeof window !== "undefined" && window.dataLayer) {
            window.dataLayer.push({
              event: "dl_login_error",
              event_action: "user_details_submit",
              error_message: message,
            });
          }
          setError({ message });
          return;
        }
        if (user["verification-status"]) {
          // User email is verified
          await getCurrentUser();
          // Track Login Success
          if (typeof window !== "undefined" && window.dataLayer) {
            window.dataLayer.push({
              event: "dl_login",
              user_id: user?.id,
              login_status: "logged",
            });
          }
          console.log("loged in successfully");
          const params = parseUrl(currentPath);
          const callbackUrl =
            customCallbackUrl ||
            get(params, ["query", "callback_uri"]) ||
            get(publisherAttributes, ["sso_login", "callback_Url"], "");
          const callbackUrlWithToast = `${callbackUrl}?toastMessage=You have successfully Signed In!`;
          const redirectUrl =
            get(params, ["query", "redirect_uri"]) || get(publisherAttributes, ["sso_login", "redirect_Url"], "");
          const allowAjax = true;
          const oauthResponse =
            ssoLoginIsEnable && (await oauthAuthorize(clientId, redirectUrl, callbackUrlWithToast, allowAjax));
          if (oauthResponse.redirect_uri) {
            window.location.href = oauthResponse.redirect_uri;
          } else {
            navigateTo(dispatch, "/");
          }
        } else {
          // User needs to validate the email account so send out an email to verify
          return sendOtp({ email: user.email })
            .then((res) => onLogin(user, res))
            .catch((error) => {
              // Track login error
              if (typeof window !== "undefined" && window.dataLayer) {
                window.dataLayer.push({
                  event: "dl_login_error",
                  event_action: "password_submit",
                  error_message: error,
                });
              }
              setError(error);
            });
        }
      })
      .catch((error) => {
        console.log("error msg", error.message);
        const errorMessage = "Invalid password";
        setError({ message: errorMessage });

        // Track login error
        if (typeof window !== "undefined" && window.dataLayer) {
          window.dataLayer.push({
            event: "dl_login_error",
            event_action: "login_details_submit",
            error_message: errorMessage,
          });
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <div styleName="auth-container passwordContainer">
      <h2 styleName="title">
        Enter the Password for <span styleName="email">{userInfo.email}</span>
        <span styleName="editIcon" onClick={() => setActiveTab("landing")}>
          <EditIcon />
        </span>
      </h2>

      <form onSubmit={loginHandler} styleName="form">
        <label htmlFor="password" styleName="auth-label">
          Enter Password
        </label>
        <div styleName="passwordWrapper">
          <input
            type={showPassword ? "text" : "password"}
            id="password"
            value={userInfo.password}
            name="Password"
            onChange={setData}
            placeholder="Enter here"
            onFocus={() => setError({ message: "" })}
            required
            styleName="auth-input"
          />
          <button type="button" onClick={handleTogglePassword} styleName="toggleButton">
            {showPassword ? <EyeClose /> : <EyeOpen />}
          </button>
        </div>

        <a
          href="#"
          styleName="forgotPassword"
          onClick={() => {
            if (typeof window !== "undefined" && window.dataLayer) {
              window.dataLayer.push({
                event: "dl_login_steps",
                event_action: "forgot_password_click",
              });
            }
            setActiveTab("forgot-password");
          }}
        >
          Forgot Password
        </a>

        {error.message ? <p styleName="error">{error.message}</p> : null}

        <button type="submit" styleName="submit-button" onClick={loginHandler}>
          Verify & Sign In
        </button>
      </form>
    </div>
  );
};

LoginBase.propTypes = {
  onLogin: func,
  setActiveTab: func,
  isLoginOpen: bool,
  customCallbackUrl: string,
  userInfo: object,
  setUserInfo: func,
  setLoading: func,
};

const mapStateToProps = (state) => ({
  isLoginOpen: get(state, ["isLoginOpen"], false),
});

const mapDispatchToProps = (dispatch) => ({
  manageLoginForm: function (payload) {
    dispatch({
      type: IS_OPEN_LOGIN_FORM,
      payload: payload,
    });
  },
});

export const Login = connect(mapStateToProps, mapDispatchToProps)(LoginBase);
